const normalizeNumberString = (v: string): string => {
  let normalized = v.replace(',', '.')
  if (normalized.startsWith('+')) {
    normalized = normalized.slice(1)
  }
  return normalized
}

const toLength = (v: number, length: number): string => {
  const stringified = v.toString()
  if (stringified.length === length) {
    return stringified
  }
  if (stringified.includes('.')) {
    return stringified.padEnd(length, '0')
  }
  return `${stringified}.`.padEnd(length, '0')
}

const toFloatOrNull = (v: unknown): number | null => {
  // Nothing to parse
  if (typeof v === 'number') {
    return isFinite(v) ? v : null
  }
  if (typeof v !== 'string') {
    return null
  }
  if (v === '') {
    return null
  }
  const normalized = normalizeNumberString(v)

  const parsed = parseFloat(normalized)
  if (!isFinite(parsed)) {
    return null
  }
  // We need to pad if trailing zeros were removed by parse
  const stringified = toLength(parsed, normalized.length)

  // Check if the parsed value converted back to string matches the normalized input
  // This ensures there are no extra characters in the input
  if (stringified !== normalized) {
    return null
  }
  return parsed
}

const normalize = (
  value: number,
  {
    min,
    max,
  }: {
    min: number
    max: number
  }
) => {
  return Math.max(min, Math.min(value, max))
}

/**
 * Rounds to specified amount of decimal places
 */
const roundDecimal = (value: number, places = 2) => {
  const scale = Math.pow(10, places)
  return Math.round(value * scale) / scale
}

/**
 * Rounds to specified amount of decimal places with ceil
 */
const ceilDecimal = (value: number, places = 2) => {
  const scale = Math.pow(10, places)
  return Math.ceil(value * scale) / scale
}

const toFloat = (v: unknown) => {
  const value = toFloatOrNull(v)
  if (value == null) {
    return 0
  }
  return value
}

const toIntOrNull = (v: unknown): number | null => {
  if (typeof v === 'number') {
    return Number.isInteger(v) ? v : null
  }
  if (typeof v !== 'string') {
    return null
  }
  const normalized = normalizeNumberString(v)
  const parsed = parseInt(normalized, 10)
  if (parsed.toString() !== normalized) {
    return null
  }

  return Number.isInteger(parsed) ? parsed : null
}

const toPositiveIntOrNull = (v: unknown): number | null => {
  const value = toIntOrNull(v)
  if (value == null || value < 0) {
    return null
  }
  return value
}

const getSafePercentage = (value: number, total: number) => {
  if (total === 0) {
    return 100
  }
  return (value / total) * 100
}

export const NumberUtils = {
  parse: {
    toFloatOrNull,
    toFloat,
    toIntOrNull,
    toPositiveIntOrNull,
  },
  round: {
    decimal: roundDecimal,
  },
  ceil: {
    decimal: ceilDecimal,
  },
  normalize,
  percentage: {
    getSafe: getSafePercentage,
  },
}
